import { useSearchParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import {
  fetchEvents,
  fetchEventOutcomes,
  fetchShowReview,
  saveShowReview,
  fetchSales,
  createSale,
  deleteSale,
} from '../apiService';
import './ProjectOverview.css';

function ProjectOverview() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [events, setEvents] = useState([]);
  // The sidebar links to a specific show, so honour ?eventId= when present
  // rather than always defaulting to the first event.
  const [searchParams, setSearchParams] = useSearchParams();
  const eventIdFromUrl = searchParams.get('eventId');
  const [selectedEventId, setSelectedEventId] = useState(eventIdFromUrl || '');
  const [outcomes, setOutcomes] = useState(null);
  const [review, setReview] = useState({
    trafficNotes: '',
    improveTrafficNotes: '',
    marketingRating: null,
    workedWell: '',
    improvementNotes: '',
    attendAgain: '',
    manualLeadCount: '',
    leadListAttachment: '',
  });
  const [uploadingLeadList, setUploadingLeadList] = useState(false);
  const [sales, setSales] = useState([]);
  const [salesExpanded, setSalesExpanded] = useState(false);
  const [showAddSale, setShowAddSale] = useState(false);
  const [newSale, setNewSale] = useState({ amount: '', saleDate: '' });
  const [loading, setLoading] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');

  // Load events on mount
  useEffect(() => {
    if (!user.userId) return;
    fetchEvents(user.userId).then(r => {
      if (r.success) setEvents(r.data);
    });
  }, [user.userId]);

  // Load all data for selected event
  useEffect(() => {
    if (!selectedEventId) {
      setOutcomes(null);
      setSales([]);
      return;
    }
    setLoading(true);
    Promise.all([
      fetchEventOutcomes(selectedEventId),
      fetchShowReview(selectedEventId),
      fetchSales(user.userId, selectedEventId),
    ]).then(([outcomesR, reviewR, salesR]) => {
      if (outcomesR.success) setOutcomes(outcomesR.data);
      if (reviewR.success && reviewR.data && reviewR.data.id) {
        setReview({
          trafficNotes: reviewR.data.trafficNotes || '',
          improveTrafficNotes: reviewR.data.improveTrafficNotes || '',
          marketingRating: reviewR.data.marketingRating || null,
          workedWell: reviewR.data.workedWell || '',
          improvementNotes: reviewR.data.improvementNotes || '',
          attendAgain: reviewR.data.attendAgain || '',
          manualLeadCount: reviewR.data.manualLeadCount || '',
          leadListAttachment: reviewR.data.leadListAttachment || '',
        });
      } else {
        // No review yet - reset to blank
        setReview({
          trafficNotes: '', improveTrafficNotes: '', marketingRating: null,
          workedWell: '', improvementNotes: '', attendAgain: '',
          manualLeadCount: '', leadListAttachment: '',
        });
      }
      if (salesR.success) setSales(salesR.data || []);
      setLoading(false);
    });
  }, [selectedEventId, user.userId]);

  async function handleSaveReview() {
    if (!selectedEventId) return;
    setSavingReview(true);
    setSavedMessage('');
    const result = await saveShowReview({
      userId: user.userId,
      eventId: selectedEventId,
      ...review,
    });
    setSavingReview(false);
    if (result.success) {
      setSavedMessage('Saved');
      setTimeout(() => setSavedMessage(''), 2000);
    } else {
      setSavedMessage('Failed to save');
    }
  }

  async function handleAddSale() {
    if (!newSale.amount || !selectedEventId) return;
    const result = await createSale({
      userId: user.userId,
      eventId: selectedEventId,
      amount: newSale.amount,
      saleDate: newSale.saleDate || null,
    });
    if (result.success) {
      setNewSale({ amount: '', saleDate: '' });
      setShowAddSale(false);
      // Refresh sales and outcomes
      const [salesR, outcomesR] = await Promise.all([
        fetchSales(user.userId, selectedEventId),
        fetchEventOutcomes(selectedEventId),
      ]);
      if (salesR.success) setSales(salesR.data || []);
      if (outcomesR.success) setOutcomes(outcomesR.data);
    }
  }

  async function handleDeleteSale(saleId) {
    if (!window.confirm('Delete this sale?')) return;
    const result = await deleteSale(saleId);
    if (result.success) {
      const [salesR, outcomesR] = await Promise.all([
        fetchSales(user.userId, selectedEventId),
        fetchEventOutcomes(selectedEventId),
      ]);
      if (salesR.success) setSales(salesR.data || []);
      if (outcomesR.success) setOutcomes(outcomesR.data);
    }
  }


  async function handleLeadListUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('File must be under 10MB.');
      return;
    }
    setUploadingLeadList(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
      const uploadRes = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
      });
      const uploadData = await uploadRes.json();
      if (!uploadData.success) {
        alert(uploadData.error || 'Upload failed.');
        setUploadingLeadList(false);
        return;
      }
      const fileName = uploadData.fileName;
      const originalName = uploadData.originalName;
      // Save the file reference on the review immediately
      const updatedReview = { ...review, leadListAttachment: `${fileName}|${originalName}` };
      setReview(updatedReview);
      // Persist to backend
      await saveShowReview({
        userId: user.userId,
        eventId: selectedEventId,
        ...updatedReview,
      });
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploadingLeadList(false);
    }
  }

  function getLeadListDisplay() {
    if (!review.leadListAttachment) return null;
    const parts = review.leadListAttachment.split('|');
    const fileName = parts[0];
    const originalName = parts[1] || fileName;
    const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
    return { url: `${API_BASE_URL}/upload?file=${fileName}`, displayName: originalName };
  }

  const fmtMoney = (v) => {
    if (v == null || v === '') return '—';
    const n = parseFloat(v);
    if (isNaN(n)) return '—';
    return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtPercent = (v) => {
    if (v == null || v === '') return '—';
    return parseFloat(v).toFixed(1) + '%';
  };

  return (
    <DashboardLayout>
      <h1 className="page-title">
        Show Review
        {selectedEventId && events.find(e => e.id === selectedEventId) && (
          <span className="page-title-context">
            {' \u2014 '}{events.find(e => e.id === selectedEventId).eventName}
          </span>
        )}
      </h1>
      <p className="page-subtitle">Post-show review and ROI tracking for a completed event.</p>

      <div className="po-event-picker">
        <label>Select Event</label>
        <select value={selectedEventId} onChange={(e) => {
          setSelectedEventId(e.target.value);
          if (e.target.value) {
            setSearchParams({ eventId: e.target.value });
          } else {
            setSearchParams({});
          }
        }}>
          <option value="">— Choose an event —</option>
          {events.map(ev => (
            <option key={ev.id} value={ev.id}>{ev.eventName}</option>
          ))}
        </select>
      </div>

      {selectedEventId && (
        <>
          {/* Section 1: Trade Show Review Summary */}
          <div className="po-section">
            <h2 className="po-section-title">Trade Show Review Summary</h2>

            <div className="po-field">
              <label>1. How was traffic throughout the show?</label>
              <textarea
                value={review.trafficNotes}
                onChange={(e) => setReview({ ...review, trafficNotes: e.target.value })}
                rows="3"
              />
            </div>

            <div className="po-field">
              <label>2. What things can we do to help improve traffic to our booth?</label>
              <textarea
                value={review.improveTrafficNotes}
                onChange={(e) => setReview({ ...review, improveTrafficNotes: e.target.value })}
                rows="3"
              />
            </div>

            <div className="po-field">
              <label>3. Rate the relevance of the marketing materials and display (1 = not relevant, 10 = perfect)</label>
              <div className="po-rating">
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <button
                    key={n}
                    type="button"
                    className={`po-rating-btn ${review.marketingRating === n ? 'active' : ''}`}
                    onClick={() => setReview({ ...review, marketingRating: n })}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div className="po-field">
              <label>4. What things did we do at this show that worked well?</label>
              <textarea
                value={review.workedWell}
                onChange={(e) => setReview({ ...review, workedWell: e.target.value })}
                rows="3"
              />
            </div>

            <div className="po-field">
              <label>5. What could we improve if we do this show in the future?</label>
              <textarea
                value={review.improvementNotes}
                onChange={(e) => setReview({ ...review, improvementNotes: e.target.value })}
                rows="3"
              />
            </div>

            <div className="po-field">
              <label>6. Should we do event again?</label>
              <select
                value={review.attendAgain}
                onChange={(e) => setReview({ ...review, attendAgain: e.target.value })}
              >
                <option value="">— Select —</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="maybe">Maybe</option>
              </select>
            </div>

            <div className="po-save-row">
              <button className="po-save-btn" onClick={handleSaveReview} disabled={savingReview}>
                {savingReview ? 'Saving...' : 'Save Review'}
              </button>
              {savedMessage && <span className="po-saved-msg">{savedMessage}</span>}
            </div>
          </div>

          {/* Section 2: Show Cost/Lead */}
          <div className="po-section">
            <h2 className="po-section-title">Show Cost / Lead</h2>

            <table className="po-metrics-table">
              <tbody>
                <tr>
                  <td className="po-metric-label">Total Expenses</td>
                  <td className="po-metric-value">{outcomes ? fmtMoney(outcomes.totalExpenses) : '—'}</td>
                </tr>
                <tr>
                  <td className="po-metric-label">Total Leads</td>
                  <td className="po-metric-value">
                    <input
                      type="number"
                      className="po-lead-input"
                      placeholder="Enter count"
                      value={review.manualLeadCount}
                      onChange={(e) => setReview({ ...review, manualLeadCount: e.target.value })}
                      min="0"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="po-metric-label po-lead-attach-label">Lead List Attachment <span className="po-optional">(optional)</span></td>
                  <td className="po-metric-value">
                    {(() => {
                      const file = getLeadListDisplay();
                      return (
                        <div className="po-lead-upload-row">
                          {file && (
                            <a href={file.url} target="_blank" rel="noopener noreferrer" className="po-lead-file-link">
                              {file.displayName}
                            </a>
                          )}
                          <label className="po-lead-upload-btn">
                            {uploadingLeadList ? 'Uploading...' : (file ? 'Replace' : 'Upload file')}
                            <input
                              type="file"
                              accept=".csv,.xlsx,.xls,.pdf"
                              onChange={handleLeadListUpload}
                              disabled={uploadingLeadList}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                      );
                    })()}
                  </td>
                </tr>
                <tr>
                  <td className="po-metric-label">Cost / Lead</td>
                  <td className="po-metric-value">{outcomes ? fmtMoney(outcomes.costPerLead) : '—'}</td>
                </tr>
                <tr>
                  <td className="po-metric-label">
                    <button
                      type="button"
                      className="po-expand-btn"
                      onClick={() => setSalesExpanded(!salesExpanded)}
                    >
                      {salesExpanded ? <FiChevronUp /> : <FiChevronDown />} Total Revenue
                    </button>
                  </td>
                  <td className="po-metric-value">{outcomes ? fmtMoney(outcomes.totalRevenue) : '—'}</td>
                </tr>
                {salesExpanded && (
                  <tr>
                    <td colSpan="2" className="po-sales-cell">
                      <div className="po-sales-list">
                        {sales.length === 0 && !showAddSale && (
                          <p className="po-sales-empty">No sales recorded yet.</p>
                        )}
                        {sales.map(sale => (
                          <div key={sale.id} className="po-sale-row">
                            <span className="po-sale-amount">{fmtMoney(sale.amount)}</span>
                            <span className="po-sale-date">{sale.saleDate || '—'}</span>
                            <button
                              className="po-sale-delete"
                              onClick={() => handleDeleteSale(sale.id)}
                              title="Delete sale"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        ))}
                        {showAddSale ? (
                          <div className="po-sale-add-form">
                            <input
                              type="number"
                              placeholder="Amount"
                              value={newSale.amount}
                              onChange={(e) => setNewSale({ ...newSale, amount: e.target.value })}
                            />
                            <input
                              type="date"
                              value={newSale.saleDate}
                              onChange={(e) => setNewSale({ ...newSale, saleDate: e.target.value })}
                            />
                            <button className="po-sale-save" onClick={handleAddSale}>Save</button>
                            <button className="po-sale-cancel" onClick={() => { setShowAddSale(false); setNewSale({ amount: '', saleDate: '' }); }}>Cancel</button>
                          </div>
                        ) : (
                          <button className="po-add-sale-btn" onClick={() => setShowAddSale(true)}>
                            <FiPlus size={14} /> Add Sale
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="po-metric-label">Show ROI</td>
                  <td className="po-metric-value po-roi">{outcomes ? fmtPercent(outcomes.roi) : '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

export default ProjectOverview;